import * as Mui from "@mui/material";
import * as MuiIcon from "@mui/icons-material";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import dayjs from "dayjs";
import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import SectionHeader from "../../../../util/component/SectionHeader";
import { ContextOperation } from "../../context/OperationContext";
import { useAgGridCustomDatasource } from "../../hooks/useAgGridCustomDatasource";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import useSnackbar from "../../hooks/useSnackbar";
import { saveFileFromResponse } from "../../utils";
import AgGridOperationTable from "../common/AgGridOperationTable";
import { FileTextWithIcon } from "../common/FileTextWithIcon";
import { nameof } from "../common/ObjectUtils";
import RfidCardImportFilterAndSearch from "./RfidCardImportFilterAndSearch";
import RfidCardImportService from "./service/RfidCardImportService";
import { RfidCardTypes } from "./tasks/Types";

const RfidCardImportTracking = () => {
    const { tenant, setLoading } = useContext(ContextOperation);
    const t = useOperationTranslation();

    const snackbar = useSnackbar();

    const form = useForm<RfidCardTypes.IFormInput>();
	
	const [refreshCount, setRefreshCount] = useState(0);

    const columnDefs: ColDef<RfidCardTypes.IRfidCardResponse>[] = [
        {
          field: nameof<RfidCardTypes.IRfidCardResponse>("filename"),
          headerName: t("rfidCardImport.rfidFile"),
          cellRenderer: (params: ICellRendererParams<RfidCardTypes.IRfidCardResponse>) => {
            return (
              <FileTextWithIcon
                onDownloadFile={() => {
                  exportFileImportTrackingDataById(params.data?.id);
                }}
                fileTextDownload={params.value}
                maxWidth="100%"
              />
            );
        },
        },
        {
          field: nameof<RfidCardTypes.IRfidCardResponse>("startDatetime"),
          headerName: t("rfidCardImport.startDate"),
          cellRenderer: (params: ICellRendererParams<RfidCardTypes.IRfidCardResponse>) => {
            return (
              <>
                {params.value ? (
                  <Mui.Box component="span" width="inherit" height="inherit">
                    {dayjs(params.value).format("DD/MM/YYYY-HH:mm")}
                  </Mui.Box>
                ) : ( <Mui.Box> </Mui.Box>)}
              </>
            );
        },
        },
        {
          field: nameof<RfidCardTypes.IRfidCardResponse>("endDatetime"),
          headerName: t("rfidCardImport.endDate"),
          cellRenderer: (params: ICellRendererParams<RfidCardTypes.IRfidCardResponse>) => {
            return (
              <>
                {params.value ? (
                  <Mui.Box component="span" width="inherit" height="inherit">
                    {dayjs(params.value).format("DD/MM/YYYY-HH:mm")}
                  </Mui.Box>
                ) : ( <Mui.Box> </Mui.Box>)}
              </>
            );
        },
        },
        {
          field: nameof<RfidCardTypes.IRfidCardResponse>("errorFilename"),
          headerName: t("rfidCardImport.failReport"),
          cellRenderer: (params: ICellRendererParams<RfidCardTypes.IRfidCardResponse>) => {
            if (!!params.value) {
                return (
                    <FileTextWithIcon
                        onDownloadFile={() => {
                          exportErrorFileImportTrackingDataById(params.data?.id);
                        }}
                        fileTextDownload={params.value}
                        fileTextColorRed={true}
                        maxWidth="100%"
                    />
                );
            } else { return params.value;}
        },
        },
        {
          field: nameof<RfidCardTypes.IRfidCardResponse>("status"),
          headerName: t("rfidCardImport.status"),
          cellRenderer: (params: ICellRendererParams<RfidCardTypes.IRfidCardResponse>) => StatusColumn(params.data),
        },
    ];

    const StatusColumn = (params: RfidCardTypes.IRfidCardResponse | undefined) => {
      if (!params) {
        return;
      }

      switch (params.status) {
        case "PROCESSED":
          return (
            <Mui.Chip
              label={t("rfidCardImport.statusType.processed")}
              variant="filled"
              color="success"
            />
          );
        case "PENDING":
          return (
            <Mui.Chip
              label={t("rfidCardImport.statusType.pending")}
              variant="filled"
              color="warning"
            />
          );
        case "WITH_ERRORS_FORMAT":
          return (
            <Mui.Chip
              label={t("rfidCardImport.statusType.errorFormat")}
              variant="filled"
              color="error"
            />
          );
        case "WITH_ERRORS_DATA_INTEGRITY":
          return (
            <Mui.Chip
              label={t("rfidCardImport.statusType.dataIntegrityError")}
              variant="filled"
              color="error"
            />
          );
        case "WITH_ERRORS_GENERIC_ERROR":
          return (
            <Mui.Chip
              label={t("rfidCardImport.statusType.genericError")}
              variant="filled"
              color="error"
            />
          );
        default: return "";
      }
    };

    const getExcel = () => {
      setLoading(true);
      
      RfidCardImportService.get.excel(tenant, form.getValues()).then(
        (response: any) => {
          setLoading(false);
          saveFileFromResponse(response);
        },
        error => {
          setLoading(false);
		  snackbar.show("error", t("rfidCardImport.errorDownloadFile"));
        }
      )
    }

    const exportFileImportTrackingDataById = async (id: number | undefined) => {
      if (!id) {
        snackbar.show("warning", "No id found for this file");
        return;
      }

      setLoading(true);
      RfidCardImportService.get.file(tenant, id).then(
        (response: any) => {
          setLoading(false);
          saveFileFromResponse(response);
        },
        error => {
          setLoading(false);
		  snackbar.show("error", t("rfidCardImport.errorDownloadFile"));
        }
      );
    };
    
    const exportErrorFileImportTrackingDataById = (id: number | undefined) => {
      if (!id) {
        snackbar.show("warning", "No id found for this file");
        return;
      }

      setLoading(true);
      RfidCardImportService.get.errorFile(tenant, id).then(
        (response: any) => {
          setLoading(false);
          saveFileFromResponse(response);
        },
        error => {
          setLoading(false);
		  snackbar.show("error", t("rfidCardImport.errorDownloadErrorFile"));
        }
      );
    };

    const [datasource, length] = useAgGridCustomDatasource(
      RfidCardImportService.get.all,
      [tenant, form.getValues()],
      [form.formState.submitCount, refreshCount]
    );

    return(
        <>
            <SectionHeader 
                title={t("rfidCardImport.titleFirst") + " " + t("rfidCardImport.titleSecond")}
            />

            <RfidCardImportFilterAndSearch form={form} />

            <Mui.Box m={2}>
                <Mui.Box>
                    <Mui.Card>
                      <Mui.Box
                          p={1}
   						  display="flex"
						  justifyContent={"flex-end"}
						  alignItems="center"
						  gap={2}
                      >
                        <Mui.Tooltip title={t("tooltipTitle.refresh")}>
                          <Mui.Button
                            variant="text"
                            size="icon"
                            onClick={() => setRefreshCount(refreshCount+1)}
                          >
                            <MuiIcon.Cached />
                          </Mui.Button>
                        </Mui.Tooltip>

                        <Mui.Tooltip title={t("tooltipTitle.download")}>
                            <Mui.Button
                              variant="text"
                              size="icon"
                              color="accent"
                              disabled={length === 0 ? true : false}
                              onClick={getExcel}
                            >
                                <MuiIcon.FileDownload />
                            </Mui.Button>
                        </Mui.Tooltip>
                      </Mui.Box>
                    </Mui.Card>
                </Mui.Box>

                <Mui.Box>
				<AgGridOperationTable
                      columnDefs={columnDefs}
                      serverSideDatasource={datasource}
                      totalElements={length}
                  />


                </Mui.Box>
            </Mui.Box>
        </>
    )
}
export default RfidCardImportTracking;
